import { Link } from 'react-router-dom';
import HeroCarousel from '@/components/feature/HeroCarousel';
import HomeSearch from '@/pages/home/components/HomeSearch';
import CategoryEntries from '@/pages/home/components/CategoryEntries';
import SectionHeading from '@/components/feature/SectionHeading';
import MovieCard from '@/components/feature/MovieCard';
import { movies } from '@/mocks/movies';
import { newsList } from '@/mocks/news';
import { getFeaturedMovies, getHotMovies, sortMovies } from '@/utils/media';

export default function Home() {
  const featured = getFeaturedMovies();
  const latest = sortMovies(movies, 'latest').slice(0, 9);
  const hot = [...getHotMovies()].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const news = [...newsList].sort((a, b) => (a.date < b.date ? 1 : -1));
  const leadNews = news[0];
  const sideNews = news.slice(1, 6);

  return (
    <div className="pb-4">
      <h1 className="sr-only">红桃影视 - 高清电影电视剧在线观看与资讯平台</h1>
      <HeroCarousel movies={featured} />
      <HomeSearch />
      <CategoryEntries />

      <section className="px-4 md:px-6 lg:px-10 mt-14">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <SectionHeading
              title="最新影视更新"
              subtitle="实时同步的电影与剧集更新动态"
              moreTo="/category/movie"
            />
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {latest.map((movie) => (
                <MovieCard key={movie.id} movie={movie} />
              ))}
            </div>
          </div>

          <div className="lg:col-span-1">
            <SectionHeading title="热播榜单" subtitle="近期口碑与热度双高" />
            <ol className="space-y-2">
              {hot.map((movie, i) => (
                <li key={movie.id}>
                  <Link
                    to={`/detail/${movie.id}`}
                    className="group flex items-center gap-3 p-2 rounded-md hover:bg-background-100 transition-colors"
                  >
                    <span
                      className={`w-6 text-center font-display text-lg ${
                        i < 3 ? 'text-primary-400' : 'text-foreground-500'
                      }`}
                    >
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <span className="w-12 h-16 rounded-md overflow-hidden shrink-0 bg-background-200">
                      <img
                        src={movie.cover}
                        alt={`${movie.title} 海报`}
                        title={`${movie.title} 热播榜`}
                        className="w-full h-full object-cover object-top"
                      />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block text-sm font-medium text-foreground-900 truncate group-hover:text-primary-400 transition-colors">
                        {movie.title}
                      </span>
                      <span className="block mt-1 text-xs text-foreground-500 truncate">
                        {movie.genres.join(' · ')} · {movie.year}
                      </span>
                    </span>
                    <span className="text-accent-500 text-xs font-semibold shrink-0">
                      {movie.rating.toFixed(1)}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="px-4 md:px-6 lg:px-10 mt-14">
        <SectionHeading
          title="影视资讯简报"
          subtitle="行业动态、热片盘点与幕后故事"
          moreTo="/news"
        />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Link
            to={`/news/${leadNews.id}`}
            className="group rounded-lg overflow-hidden bg-background-100 border border-background-300/60 hover:border-primary-500/50 transition-colors"
          >
            <div className="relative w-full aspect-[16/9] overflow-hidden">
              <img
                src={leadNews.cover}
                alt={leadNews.title}
                title={`${leadNews.title} 资讯`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
              />
              <span className="absolute top-3 left-3 px-3 py-1 rounded-full bg-accent-500 text-background-50 text-xs font-semibold">
                {leadNews.category}
              </span>
            </div>
            <div className="p-5">
              <h3 className="text-lg font-semibold text-foreground-950 group-hover:text-primary-400 transition-colors line-clamp-2">
                {leadNews.title}
              </h3>
              <p className="mt-2 text-sm text-foreground-600 line-clamp-2">{leadNews.summary}</p>
              <p className="mt-3 text-xs text-foreground-500 flex items-center gap-3">
                <span>
                  <i className="ri-user-line mr-1" />
                  {leadNews.author}
                </span>
                <span>
                  <i className="ri-calendar-line mr-1" />
                  {leadNews.date}
                </span>
              </p>
            </div>
          </Link>

          <ul className="flex flex-col gap-3">
            {sideNews.map((item) => (
              <li key={item.id}>
                <Link
                  to={`/news/${item.id}`}
                  className="group flex items-start gap-3 p-4 rounded-lg bg-background-100 border border-background-300/60 hover:border-primary-500/50 transition-colors"
                >
                  <span className="w-16 h-16 rounded-md overflow-hidden shrink-0 bg-background-200">
                    <img
                      src={item.cover}
                      alt={item.title}
                      title={`${item.title} 资讯`}
                      className="w-full h-full object-cover object-top"
                    />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2">
                      {item.title}
                    </span>
                    <span className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
                      <span className="text-accent-500">{item.category}</span>
                      <span>{item.date}</span>
                    </span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}